/* eslint-disable @typescript-eslint/ban-ts-comment */
import '../styles/globals.css';

import dynamic from 'next/dynamic';
import WithAuth from '@components/Auth/WithAuth';
import { StreamDataProvider } from '@components/context/StreamContext';
import { TeamsDataProvider } from '@components/context/TeamsContext';
import { ZoomDataProvider } from '@zoom-app/components/contexts/ZoomContext';
import { MicrosoftTeamsDataProvider } from '@teams-app/components/contexts/MicrosoftTeamsContext';
import { OfficeDataProvider } from 'content-add-in/components/contexts/OfficeContext';
import { getLayout } from '@components/Layout/LayoutConfiguration';
import Head from 'next/head';
import ErrorBoundaryWrapper from '@components/Errors/ErrorBoundaryWrapper';
import { ThemeProvider } from 'next-themes';
import GoogleTagManager from '@components/common/GoogleTagManager';
import { HeroUIProvider } from "@heroui/react";

import {
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CelloComponent from '@components/celloComponent';
import { GoogleOAuthProvider } from '@react-oauth/google';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { PopupProvider } from '@components/context/screenShotPupUpContext';
import { ProduktlyProvider } from '@lib/providers/Produktly';

const HiddenZoomComponent = dynamic(
  () => import('@components/HiddenZoomComponent'),
  { ssr: false }
);

const queryClient = new QueryClient();

// Check that PostHog is client-side (used to handle Next.js SSR)
if (typeof window !== 'undefined' && process.env.NEXT_PUBLIC_POSTHOG_KEY) {
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
    api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
    person_profiles: 'always',
    // Enable debug mode in development
    loaded: (posthog) => {
      if (process.env.NEXT_PUBLIC_APP_ENV === 'development') posthog.debug(false);
    }
  });
}

const OfficeLoader = dynamic(
  () => import('@components/OfficeLoader'),
  { ssr: false }
);

function MyApp({ Component, pageProps, router }) {
  const layout = getLayout(router.pathname);

  return (
    <div style={{ overflowX: 'hidden' }}>
      <Head>
        <link rel="favicon" href="/images/streamAlive-favicon.png" />
        <title>StreamAlive</title>
        {/* <script
          src="https://cdn.jsdelivr.net/npm/react-render-tracker"
          data-config="inpage:false"
        ></script> */}
        {!['/login', '/signup', '/sandbox/interactions-generator', '/sandbox/audience-engagement-ai'].includes(router.pathname) && <meta name="viewport" content="width=1440" />}
      </Head>
      <OfficeLoader>
        <PostHogProvider client={posthog}>
          <ThemeProvider attribute="class" enableSystem={false} enableColorScheme={false} defaultTheme="light" forcedTheme={Component.theme || undefined} >
            <HeroUIProvider>
              <QueryClientProvider client={queryClient}>
                <ErrorBoundaryWrapper>
                  <TeamsDataProvider>
                    <StreamDataProvider>
                      <ZoomDataProvider>
                        <MicrosoftTeamsDataProvider>
                          <OfficeDataProvider>
                            <PopupProvider>
                              <WithAuth router={router}>
                                <GoogleTagManager>
                                  <CelloComponent>
                                    <GoogleOAuthProvider clientId={process.env.NEXT_PUBLIC_AUTH_GOOGLE_ID}>
                                      <ProduktlyProvider>
                                        {layout({ children: <Component {...pageProps} /> })}
                                        <ToastContainer position='bottom-left' theme='colored' />
                                      </ProduktlyProvider>
                                    </GoogleOAuthProvider>
                                  </CelloComponent>
                                </GoogleTagManager>
                              </WithAuth>
                            </PopupProvider>
                          </OfficeDataProvider>
                        </MicrosoftTeamsDataProvider>
                      </ZoomDataProvider>
                    </StreamDataProvider>
                  </TeamsDataProvider>
                </ErrorBoundaryWrapper>
                <ReactQueryDevtools initialIsOpen={false} />
              </QueryClientProvider>
            </HeroUIProvider>
          </ThemeProvider>
        </PostHogProvider>
      </OfficeLoader>
      <HiddenZoomComponent />
    </div>
  );
}
export default MyApp;
